<template>
	<div class="oauth-consent">
		<Card class="oauth-consent__card">
			<template #header>
				<div class="oauth-consent__logo-container">
					<img
						alt="codecannon logo"
						class="oauth-consent__logo"
						src="@/assets/logo.svg" />
				</div>
			</template>
			<template #title>{{ consentTitle }}</template>
			<template #content>
				<div class="oauth-consent__content">
					<p
						v-if="!consentOutcome"
						class="oauth-consent__description">
						<strong>{{ clientName }}</strong> is requesting access to your CodeCannon MCP server.
					</p>

					<div
						v-if="!consentOutcome"
						class="oauth-consent__meta">
						<p><strong>Signed in as:</strong> {{ authStore.user?.email ?? 'Guest' }}</p>
						<p><strong>Resource:</strong> {{ resourceLabel }}</p>
						<p><strong>Callback:</strong> {{ redirectUriLabel }}</p>
					</div>

					<div
						v-if="!consentOutcome"
						class="oauth-consent__scopes">
						<span
							v-for="scope in scopeList"
							:key="scope"
							class="oauth-consent__scope-pill">
							{{ scope }}
						</span>
					</div>

					<Message
						v-if="!authStore.user"
						class="oauth-consent__status-message"
						severity="warn"
						:closable="false">
						Please sign in to continue with this authorization request.
					</Message>

					<Message
						v-else-if="submitError"
						class="oauth-consent__status-message"
						severity="error"
						:closable="false">
						{{ submitError }}
					</Message>

					<Message
						v-else-if="consentOutcome"
						class="oauth-consent__status-message"
						:closable="false"
						:severity="consentOutcome.severity">
						{{ consentOutcome.message }}
					</Message>

					<p
						v-if="manualRedirectUrl && consentOutcome"
						class="oauth-consent__manual-link">
						Automatic redirect was blocked. <a :href="manualRedirectUrl">Return to your client</a>.
					</p>

					<Divider v-if="!consentOutcome" />

					<div
						v-if="!authStore.user"
						class="oauth-consent__single-action">
						<Button
							class="oauth-consent__button"
							icon="fal fa-right-to-bracket"
							label="Go to Sign In"
							@click="goToLogin" />
					</div>

					<div
						v-else-if="!consentOutcome"
						class="oauth-consent__actions">
						<Button
							class="oauth-consent__button"
							label="Authorize"
							type="submit"
							:disabled="isSubmittingDecision !== null"
							:loading="isSubmittingDecision === 'approve'"
							@click="submitConsent('approve')" />
						<Button
							class="oauth-consent__button"
							label="Decline"
							severity="secondary"
							type="submit"
							:disabled="isSubmittingDecision !== null"
							:loading="isSubmittingDecision === 'deny'"
							@click="submitConsent('deny')" />
					</div>
				</div>
			</template>
		</Card>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/Auth'
import AuthApi from '@/helpers/api/AuthApi'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Message from 'primevue/message'
import Divider from 'primevue/divider'

type ConsentDecision = 'approve' | 'deny'
type ConsentSeverity = 'success' | 'warn' | 'error'

interface ConsentOutcome {
	severity: ConsentSeverity
	message: string
}

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const authApi = new AuthApi()

const submitError = ref('')
const isSubmittingDecision = ref<ConsentDecision | null>(null)
const consentOutcome = ref<ConsentOutcome | null>(null)
const manualRedirectUrl = ref('')

const clientName = computed(() => queryString(route.query.client_name) ?? 'CodeCannon MCP Client')
const resourceLabel = computed(() => getHostOrValue(queryString(route.query.resource) ?? 'Unknown'))
const redirectUriLabel = computed(() =>
	getHostOrValue(queryString(route.query.redirect_uri) ?? 'Unknown'),
)
const scopeList = computed(() => {
	const scope = queryString(route.query.scope)
	if (!scope) return ['mcp:use']

	return scope
		.split(' ')
		.map((value) => value.trim())
		.filter(Boolean)
})

const consentTitle = computed(() => {
	if (!consentOutcome.value) {
		return 'Authorize Access'
	}

	if (consentOutcome.value.severity === 'error') {
		return 'Authorization Failed'
	}

	return 'Authorization Complete'
})

function goToLogin() {
	router.push({
		name: 'login',
		query: {
			redirect: route.fullPath,
		},
	})
}

async function submitConsent(decision: ConsentDecision) {
	if (isSubmittingDecision.value !== null) {
		return
	}

	isSubmittingDecision.value = decision
	submitError.value = ''
	consentOutcome.value = null

	let redirect = null

	try {
		const response =
			decision === 'approve' ? await authApi.oauthApprove() : await authApi.oauthDeny()

		redirect = response.data.redirect_url

		consentOutcome.value = {
			severity: decision === 'approve' ? 'success' : 'warn',
			message:
				decision === 'approve'
					? 'Authorization accepted. Return to your client.'
					: 'Authorization declined. Return to your client.',
		}
	} catch (error: any) {
		consentOutcome.value = {
			severity: 'error',
			message:
				error?.response?.data?.message ||
				'Unable to complete authorization. Restart the OAuth flow from your MCP client.',
		}
	} finally {
		isSubmittingDecision.value = null

		if (redirect) {
			window.location.replace(redirect)
		}
	}
}

function queryString(value: unknown): string | null {
	if (typeof value === 'string') return value
	if (Array.isArray(value) && typeof value[0] === 'string') return value[0]
	return null
}

function getHostOrValue(value: string): string {
	try {
		return new URL(value).host
	} catch {
		return value
	}
}
</script>

<style scoped lang="scss">
.oauth-consent {
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100vh;
	width: 100%;

	.oauth-consent__card {
		width: 500px;
		overflow: hidden;

		.oauth-consent__content {
			display: flex;
			flex-direction: column;
			gap: 8px;

			& > * {
				width: 100%;
			}
		}
	}

	.oauth-consent__logo-container {
		display: flex;
	}

	.oauth-consent__logo {
		padding-left: 45px;
		padding-right: 45px;
		padding-bottom: 20px;
		padding-top: 20px;
		background: #eeff01;
		margin: auto;
		display: block;
		max-width: 100%;
	}

	.oauth-consent__description {
		margin: 0;
		font-size: 14px;
		line-height: 1.5;
	}

	.oauth-consent__manual-link {
		margin: 0;
		font-size: 13px;
		line-height: 1.4;

		a {
			color: var(--p-primary-color);
		}
	}

	.oauth-consent__meta {
		display: flex;
		flex-direction: column;
		gap: 8px;
		padding: 10px 12px;
		border: 1px solid var(--p-content-border-color);
		border-radius: 8px;
		background: var(--p-surface-900);

		p {
			margin: 0;
			font-size: 13px;
			line-height: 1.4;
			word-break: break-word;
		}
	}

	.oauth-consent__scopes {
		display: flex;
		flex-wrap: wrap;
		gap: 8px;
	}

	.oauth-consent__scope-pill {
		font-size: 12px;
		font-weight: 600;
		padding: 6px 10px;
		border-radius: 999px;
		border: 1px solid var(--p-content-border-color);
		background: var(--p-surface-900);
	}

	.oauth-consent__status-message {
		margin: 8px 0;
	}

	.oauth-consent__actions {
		display: flex;
		gap: 8px;

		.oauth-consent__action-form {
			flex: 1;
		}
	}

	.oauth-consent__single-action {
		flex: 1;
	}

	.oauth-consent__button {
		width: 100%;
	}

	@media (max-width: 560px) {
		padding: 12px;

		.oauth-consent__card {
			width: 100%;
		}

		.oauth-consent__actions {
			flex-direction: column;
		}

		.oauth-consent__logo {
			padding-left: 28px;
			padding-right: 28px;
		}
	}
}
</style>
